import React, { useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { useTheme } from "@mui/material/styles";

import { useGlobalState } from "../common/GlobalContext";
import { mapFocusCategories, COLOR_LINES } from "../common/constants";

// Overlays whose map signage is a styled line (not an icon). Keep in sync with
// the .setStyle({...}) calls in the matching *BoundaryLayer components.
const LINE_STYLE_LEGEND = {
  "MDOT SHA County Boundaries": { color: "#ffa305", weight: 2 },
  "MDOT SHA District Boundaries": { color: "#6495ED", weight: 4 },
};

export function QuickInfoControl({
  position,
  selectedMapLineCategory,
  minMaxValues,
  info,
  mode,
  routeId,
  toFrom,
  PopupData,
  activeOverlays = [],
  activeBaseLayer = "",
}) {
  const query = useGlobalState();
  const theme = useTheme();
  const [collapsed, setCollapsed] = useState(false);

  // Dark mode: App's `mode` prop is the source of truth; palette mode is a
  // fallback. OR so either signal turns the legend dark.
  const isDark = mode === "dark" || theme.palette.mode === "dark";
  const surface = isDark ? "#1f1f1f" : "#ffffff";
  const textColor = isDark ? "#e8e8e8" : "#212121";
  const subText = isDark ? "#b5b5b5" : "#555555";
  const borderCol = isDark ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.15)";

  const layerName =
    query.layerSpecs?.layer || query.statsLayerDesc || "No layer selected";

  const POSITION_CLASSES = {
    bottomleft: "leaflet-bottom leaflet-left",
    bottomright: "leaflet-bottom leaflet-right",
    topleft: "leaflet-top leaflet-left",
    topright: "leaflet-top leaflet-right",
  };
  const positionClass =
    (position && POSITION_CLASSES[position]) || POSITION_CLASSES.topright;

  // Fixed-width swatch cell so every label lines up in one column.
  const SWATCH_W = 34;

  const Row = ({ swatch, label }) => (
    <Box sx={{ display: "flex", alignItems: "center", my: "3px" }}>
      <Box
        sx={{
          width: SWATCH_W,
          minWidth: SWATCH_W,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          mr: 1,
        }}
      >
        {swatch}
      </Box>
      <Box sx={{ fontSize: 13, lineHeight: 1.25, color: textColor }}>{label}</Box>
    </Box>
  );

  const lineSwatch = (color, height = 4) => (
    <span
      style={{
        display: "inline-block",
        width: SWATCH_W,
        height,
        background: color,
        borderRadius: 2,
      }}
    />
  );

  // One active-overlay row: colored line for boundary layers, icon for icon
  // overlays (icon rendered FIRST, then its label), plain text otherwise.
  const OverlayRow = ({ name }) => {
    const lineStyle = LINE_STYLE_LEGEND[name];
    if (lineStyle) {
      return <Row swatch={lineSwatch(lineStyle.color, lineStyle.weight)} label={name} />;
    }

    // Overlay names are "Label <svg .../>": split so the icon comes first.
    const idx = name.search(/<[a-z]/i);
    if (idx !== -1) {
      const label = name.slice(0, idx).trim();
      const iconHtml = name.slice(idx);
      return (
        <Row
          swatch={
            <span
              style={{ display: "inline-flex", alignItems: "center" }}
              dangerouslySetInnerHTML={{ __html: iconHtml }}
            />
          }
          label={label}
        />
      );
    }

    return <Row swatch={null} label={name} />;
  };

  function LegendControl() {
    const focusMatch = mapFocusCategories.find(
      (itm) =>
        itm.category === query.geoRoute && itm.description === query.mapFocus
    );
    let categoryDesc = focusMatch ? focusMatch.description : query.mapFocus || "None";
    if (query.vehicleType === "truck" && query.mapFocus !== "None")
      categoryDesc = "Truck " + categoryDesc;

    const maxValue = minMaxValues.max;
    const minValue = minMaxValues.min;
    const increm = (maxValue - minValue) * 0.2;

    const LEGEND_COLORS =
      selectedMapLineCategory === "dlyrank"
        ? [
          { color: "red", label: "1 - 10" },
          { color: "orange", label: "11 - 25" },
          { color: "yellow", label: "26 - 50" },
          { color: "blue", label: "51 - 100" },
          { color: "green", label: "101 +" },
        ]
        : [
          { color: "red", label: `${(minValue + increm * 4).toLocaleString()} +` },
          { color: "orange", label: `${(minValue + increm * 3).toLocaleString()} - ${(minValue + increm * 4).toLocaleString()}` },
          { color: "yellow", label: `${(minValue + increm * 2).toLocaleString()} - ${(minValue + increm * 3).toLocaleString()}` },
          { color: "blue", label: `${(minValue + increm * 1).toLocaleString()} - ${(minValue + increm * 2).toLocaleString()}` },
          { color: "green", label: `< ${(minValue + increm * 1).toLocaleString()}` },
        ];

    const overlayItems = activeOverlays.filter((n) => n && n !== "Roads");

    return (
      <Box sx={{ width: 300, color: textColor }}>


        {selectedMapLineCategory !== "none"
          ? LEGEND_COLORS.map(({ color, label }) => (
              <Row key={color + label} swatch={lineSwatch(color)} label={label} />
            ))
          : <Row swatch={lineSwatch(COLOR_LINES.normal)} label="All roads" />}

        {/* Active overlay layers with their icon / line signage */}
        {overlayItems.length > 0 && (
          <>
            <Box sx={{ fontSize: 13, fontWeight: 600, mt: "8px", mb: "2px", color: textColor }}>
              Active layers
            </Box>
            {overlayItems.map((name) => (
              <OverlayRow key={name} name={name} />
            ))}
          </>
        )}
      </Box>
    );
  }

  const hasRoadInfo =
    typeof info.route_id !== "undefined" ||
    typeof info.seg_name !== "undefined";

  return (
    <div className={positionClass} style={{ marginBottom: "-7.5px" }}>
      <div
        className="leaflet-control leaflet-bar"
        style={{
          backgroundColor: surface,
          color: textColor,
          borderRadius: 4,
          boxShadow: "0 1px 5px rgba(0,0,0,0.4)",
        }}
      >
        {/*  Header / toggle bar  */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 1,
            py: 0.25,
            cursor: "pointer",
            userSelect: "none",
            backgroundColor: surface,
            color: textColor,
            borderBottom: collapsed ? "none" : `1px solid ${borderCol}`,
            borderRadius: collapsed ? "4px" : "4px 4px 0 0",
          }}
          onClick={() => setCollapsed((v) => !v)}
        >
          <Typography
            variant="caption"
            sx={{ fontWeight: 600, fontSize: 12, color: textColor }}
          >
            Segment Info &amp; Legend
          </Typography>
          <IconButton size="small" tabIndex={-1} sx={{ p: 0, ml: 1, color: textColor }}>
            {collapsed ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        </Box>

        {/*  Collapsible body  */}
        <Collapse in={!collapsed} timeout="auto">
          <div
            className={"details " + (mode || "")}
            style={{ padding: "4px 8px", backgroundColor: surface, color: textColor }}
          >
            {!hasRoadInfo && (
              <Typography variant="subtitle1" sx={{ color: textColor }}>
                Hover over a road to see data
              </Typography>
            )}

            {hasRoadInfo && (
              <div>
                <p className="left-text" style={{ color: textColor }}>
                  <strong>Route ID:</strong> {routeId}
                </p>
                <p className="left-text" style={{ color: textColor }}>
                  <strong>From/To:</strong> {toFrom}
                </p>

                {query.directionality === "crossSectional" &&
                  query.vehicleType === "all" &&
                  (query.geoRoute === "hpms/geojson" ||
                    query.geoRoute === "hpms_segments/geojson") && (
                    <PopupData type="all" />
                  )}
                {query.directionality === "crossSectional" &&
                  query.vehicleType === "all" &&
                  query.geoRoute === "top100/geojson" && (
                    <PopupData type="top100" />
                  )}
                {query.directionality === "directional" &&
                  query.vehicleType === "all" &&
                  query.geoRoute === "top100/geojson" && (
                    <PopupData type="top100-directional" />
                  )}
                {query.directionality === "crossSectional" &&
                  query.vehicleType === "truck" &&
                  (query.geoRoute === "hpms/geojson" ||
                    query.geoRoute === "hpms_segments/geojson") && (
                    <PopupData type="truck" />
                  )}
                {query.directionality === "crossSectional" &&
                  query.vehicleType === "truck" &&
                  query.geoRoute === "top100/geojson" && (
                    <PopupData type="top100truck" />
                  )}
                {query.directionality === "directional" &&
                  query.vehicleType === "truck" &&
                  query.geoRoute === "top100/geojson" && (
                    <PopupData type="top100truck-directional" />
                  )}
                {query.directionality === "directional" &&
                  query.vehicleType === "all" &&
                  (query.geoRoute === "hpms/geojson" ||
                    query.geoRoute === "hpms_segments/geojson") && (
                    <div className="directional">
                      <div className="direction-1">
                        <div className="direction-title">
                          <strong>{info.direction1}</strong>
                        </div>
                        <PopupData type="all1" />
                      </div>
                      <div className="direction-2">
                        <div className="direction-title">
                          <strong>{info.direction2}</strong>
                        </div>
                        <PopupData type="all2" />
                      </div>
                    </div>
                  )}
                {query.directionality === "directional" &&
                  query.vehicleType === "truck" &&
                  (query.geoRoute === "hpms/geojson" ||
                    query.geoRoute === "hpms_segments/geojson") && (
                    <div className="directional">
                      <div className="direction-1">
                        <div className="direction-title">
                          <strong>{info.direction1}</strong>
                        </div>
                        <PopupData type="truck1" />
                      </div>
                      <div className="direction-2">
                        <div className="direction-title">
                          <strong>{info.direction2}</strong>
                        </div>
                        <PopupData type="truck2" />
                      </div>
                    </div>
                  )}
              </div>
            )}

            <LegendControl />
          </div>
        </Collapse>
      </div>
    </div>
  );
}